import {
  Button,
  Input,
  InputRef,
  message,
  Modal,
  Space,
  Switch,
  Table,
  TableColumnType,
  Tooltip,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  FormOutlined,
  PlusSquareOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import cx from 'classnames';
import {
  TQuestionBankCategory,
  EQuestionType,
  questionTypeList,
  TQuestion,
} from '@/types/QuestionBank';
import {
  deleteQuestion,
  deleteQuestionBankCategory,
  enableQuestion,
  listCampRelation,
  pageQuestion,
  updateQuestionBankCategory,
} from '@/services/QuestionBank';
import { useRequest } from '@@/plugin-request/request';
import { TablePaginationConfig } from 'antd/lib/table';
import QuestionFormModal from '@/pages/backend/QuestionSettings/QuestionDetails/QuestionForm';
import styles from './index.less';
import { FilterDropdownProps, type FilterValue } from 'antd/es/table/interface';
import { Link } from 'umi';
import { ESwitch } from '@/types/interface';
import { isEmpty } from 'lodash';

const QuestionDetails = ({
  isEditingTitle,
  setIsEditingTitle,
  category,
  refresh,
  setRefresh,
}: {
  isEditingTitle?: boolean;
  setIsEditingTitle: (isEditingTitle: boolean) => void;
  category?: TQuestionBankCategory;
  refresh: number;
  setRefresh: (refresh: number) => void;
}) => {
  const [name, setName] = useState<string>();
  const [data, setData] = useState<TQuestion[]>([]);
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<TQuestion>();
  const [refreshPage, setRefreshPage] = useState<number>(0);
  const [empty, setEmpty] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const searchInput = useRef<InputRef>(null);
  const inputRef = useRef(null);
  const [isEditQuestion, setIsEditQuestion] = useState<boolean>(false);

  if (isEditingTitle) {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm']) => {
    confirm();
    setSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (): TableColumnType<TQuestion> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`搜索题目标题`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            搜索
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            重置
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            关闭
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  useEffect(() => {
    if (category) {
      setName(category.name);
      pageQuestionRun();
      setEmpty(false);
    } else {
      setEmpty(true);
    }
  }, [category]);

  useEffect(() => {
    pageQuestionRun();
  }, [refreshPage, searchText]);

  const { run: updateQuestionBankCategoryRun } = useRequest(
    (form: TQuestionBankCategory) => {
      return updateQuestionBankCategory(category?.id!, form);
    },
    {
      manual: true,
      onSuccess: (data) => {
        setName(data.name);
        setRefresh(refresh + 1);
      },
    },
  );

  const { run: listCampRelationRun } = useRequest((id: string) => listCampRelation(id), {
    manual: true,
  });

  const columns = [
    {
      title: '序号',
      dataIndex: 'key',
      width: '65px',
      render: (_value: any, _record: any, index: number) =>
        `${(pagination.current! - 1) * pagination.pageSize! + index + 1}`,
    },
    {
      title: '题目标题',
      dataIndex: 'title',
      key: 'title',
      render: (value: string, record: any) => (
        <Typography.Text ellipsis={{ tooltip: value }} style={{ maxWidth: '392px' }}>
          <Link
            onClick={() => {
              setCurrentQuestion(record);
              setIsEditQuestion(true);
              setIsModalOpen(true);
            }}
            to={undefined}
          >
            {value}
          </Link>
        </Typography.Text>
      ),
      ...getColumnSearchProps(),
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      key: 'type',
      render: (value: EQuestionType) => {
        return questionTypeList.find((item) => item.value === value)?.text;
      },
      filters: questionTypeList,
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: 160,
    },
    {
      title: '是否启用',
      dataIndex: 'enable',
      key: 'enable',
      width: 100,
      render: (value: boolean, record: TQuestion, index: number) => (
        <Switch
          value={value}
          onChange={(e) => {
            enableQuestionRun(record.id!, e ? ESwitch.OPEN : ESwitch.CLOSE);
          }}
        ></Switch>
      ),
    },
    {
      title: '操作',
      key: 'operate',
      width: 80,
      render: (value: undefined, record: TQuestion, index: number) => (
        <DeleteOutlined
          onClick={() => {
            Modal.confirm({
              title: '确认删除该题目吗？',
              content:
                '题目删除后无法恢复，并有可能导致引用题目分类的地方无法按预期抽题，请谨慎操作',
              icon: <QuestionCircleOutlined />,
              okText: '删除',
              okButtonProps: { type: 'primary', danger: true },
              cancelText: '取消',
              onOk: () => {
                if (record.id) {
                  deleteQuestionRun(record.id);
                }
              },
            });
          }}
        />
      ),
    },
  ];

  const { run: deleteQuestionBankCategoryRun } = useRequest(
    (categoryId: string) => {
      return deleteQuestionBankCategory(categoryId);
    },
    {
      manual: true,
      onSuccess: () => {
        setRefresh(refresh + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const { loading, run: pageQuestionRun } = useRequest(
    () => {
      return pageQuestion({
        current: pagination.current!,
        size: pagination.pageSize!,
        categoryId: category?.id!,
        types: filters?.['type'] as EQuestionType[],
        key: searchText,
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        setData(data.records);
        setPagination({
          ...pagination,
          total: data.total,
        });
      },
    },
  );

  const { run: deleteQuestionRun } = useRequest(
    (id: string) => {
      return deleteQuestion(id);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('删除成功');
        setRefreshPage(refreshPage + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  const { run: enableQuestionRun } = useRequest(
    (id: string, enable: ESwitch) => {
      return enableQuestion(id, enable);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('修改成功');
        setRefreshPage(refreshPage + 1);
      },
      onError: (err) => {
        message.error(err.message);
      },
    },
  );

  return (
    <>
      {!empty ? (
        <div>
          <div className={cx('m-[24px]')}>
            <div>
              {isEditingTitle ? (
                <div>
                  <Input
                    ref={inputRef}
                    value={name}
                    showCount
                    maxLength={20}
                    style={{ width: 400 }}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <CheckOutlined
                    style={{ color: '#1890FF', marginLeft: 20, cursor: 'pointer' }}
                    onClick={() => {
                      if (name) {
                        updateQuestionBankCategoryRun({
                          name,
                          pid: category?.pid,
                          index: category?.index,
                        });
                      }
                      setIsEditingTitle(false);
                    }}
                  />
                  <CloseOutlined
                    style={{ color: '#FF4D4F', marginLeft: 20, cursor: 'pointer' }}
                    onClick={() => {
                      setIsEditingTitle(false);
                      setName(category?.name || '');
                    }}
                  />
                </div>
              ) : (
                <div>
                  <span className={cx('flex-1', 'text-base', 'font-medium')}>{name}</span>
                  <Tooltip placement="top" title="重命名">
                    <FormOutlined
                      className={cx('w-[14px]', 'text-[#808696]', 'ml-[21px]')}
                      onClick={() => setIsEditingTitle(true)}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title="删除">
                    <DeleteOutlined
                      className={cx('w-[14px]', 'text-[#808696]', 'ml-[18px]')}
                      onClick={() => {
                        listCampRelationRun(category?.id!).then((res) => {
                          if (!isEmpty(res)) {
                            Modal.warning({
                              title: '该题目分类被以下打卡营使用，无法删除',
                              content: (
                                <div>
                                  {res.map((item) => (
                                    <div className={'text-[#595959]'}>{item.name}</div>
                                  ))}
                                </div>
                              ),
                              okText: '我知道了',
                            });
                          } else {
                            Modal.confirm({
                              title: '确认删除该题目分类吗？',
                              icon: <QuestionCircleOutlined />,
                              content: '分类下的题目将一并删除，删除后无法恢复，请谨慎操作',
                              okButtonProps: { type: 'primary', danger: true },
                              okText: '删除',
                              okType: 'danger',
                              cancelText: '取消',
                              onOk() {
                                if (category) {
                                  deleteQuestionBankCategoryRun(category.id!);
                                }
                                setIsEditingTitle(false);
                                setEmpty(true);
                              },
                            });
                          }
                        });
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
            <div className={cx('mt-[24px]')}>
              <Button
                type={'primary'}
                icon={<PlusSquareOutlined />}
                onClick={() => {
                  setCurrentQuestion({
                    categoryId: category?.id!,
                  });
                  setIsEditQuestion(false);
                  setIsModalOpen(true);
                }}
              >
                新建题目
              </Button>
            </div>
            <div className={cx('mt-[16px]')}>
              <Table
                className={styles.TableWrapper}
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={{
                  ...pagination,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  showTotal: (total) => `共 ${total} 条数据`,
                }}
                onChange={(pagination, filters) => {
                  setPagination(pagination);
                  setRefreshPage(refreshPage + 1);
                  setFilters(filters);
                }}
              />
            </div>
          </div>
          <div>
            <QuestionFormModal
              isEdit={isEditQuestion}
              propsData={currentQuestion}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              refresh={refreshPage}
              setRefresh={setRefreshPage}
            />
          </div>
        </div>
      ) : (
        <div className={cx('text-center', 'mt-[160px]')}>
          <img
            alt="warning1"
            src="https://hwobs.fanruan.com/ui/warning1.png"
            className={cx('w-[160px]', 'h-[160px]')}
          />
          <p className={cx('text-[#C0C0C0]', 'text-[14px]', 'font-normal')}>
            请先在左侧选择题目分类
          </p>
        </div>
      )}
    </>
  );
};

export default QuestionDetails;
